/**
 * Business Settings Page Component
 * Manages business information and social media links
 * @component BusinessSettings
 */

import React, { useState, useEffect } from 'react';
import Collapse from '@/components/Collapse/Collapse';
import MultipleSelect from '@/components/MultipleSelect/MultipleSelect';
import { BusinessGallery } from '@/components/BusinessGallery/BusinessGallery';
import ImageIcon from '@mui/icons-material/Image';
import { BusinessModal } from '@/modals/BusinessModal';
import { createBusiness, updateBusiness, getBusinessSettings } from '@/services/businessService';
import Popconfirm from '@/components/Popconfirm/Popconfirm';
import BusinessStatusIndicator from '@/components/BusinessStatusIndicator/BusinessStatusIndicator';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNotification } from '@/contexts/NotificationContext';
import { translate } from '@/locales';
import { useBusinessCreationStatus } from '@/hooks/usePermissions';
import { getServices } from '@/services/serviceService';
import { getCategories, Category } from '@/services/categoryService';
import { openGoogleMaps } from '@/utils/mapsUtils';
import { formatPhoneNumber, getCleanPhoneNumber } from '@/utils/phoneUtils';
import Tooltip from '@/components/Tooltip';
import './BusinessSettings.css';

// Removed unused BusinessInfo interface

// Removed unused DEFAULT_BUSINESS_INFO

const BusinessSettings: React.FC = () => {
  const { language } = useLanguage();
  const { addNotification } = useNotification();
  const t = (key: any) => translate(key, language as 'en' | 'ua');
  const { status: businessCreationStatus, loading: permissionsLoading, refresh: refreshBusinessCreationStatus } = useBusinessCreationStatus();

  const [activeTab, setActiveTab] = useState<'settings' | 'preview'>('settings');

  // Removed unused businessInfo and setBusinessInfo
  const [businessList, setBusinessList] = useState<any[]>([]);
  const [popconfirmOpen, setPopconfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<any | null>(null);
  const [editingBusinessLocal, setEditingBusinessLocal] = useState<any | null>(null);
  const [servicesOptions, setServicesOptions] = useState<{ id: number; name: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Category[]>([]);
  const [previewDataLoaded, setPreviewDataLoaded] = useState(false);

  // Fetch categories and services on mount (needed for settings tab)
  useEffect(() => {
    getCategories().then(setCategoryOptions);
    getServices().then(setServicesOptions);
  }, []);

  // Fetch additional data only for preview tab
  useEffect(() => {
    if (activeTab === 'preview') {
      setPreviewDataLoaded(true); // Services are already loaded
    } else {
      setPreviewDataLoaded(false);
    }
  }, [activeTab]);
  // Removed unused states: categories, servicesList, isUpdating, error, saved

  // Fetch business list
  const fetchBusinessList = () => {
    const token = localStorage.getItem('auth_token');
    getBusinessSettings(token).then((data) => {
      setBusinessList(Array.isArray(data) ? data : [data]);
    });
  };

  useEffect(() => {
    fetchBusinessList();
  }, []);

  const handleAddBusiness = async (data: any) => {
    try {
      const token = localStorage.getItem("auth_token");
      await createBusiness(data, token);
      setShowModal(false);
      fetchBusinessList();
      refreshBusinessCreationStatus();
    } catch (error) {
      console.error("Error in handleAddBusiness:", error);
    }
  };

  const handleEditBusiness = async (data: any) => {
    try {
      const token = localStorage.getItem("auth_token");
      await updateBusiness({ ...editingBusiness, ...data }, token);
      setEditingBusiness(null);
      setEditingBusinessLocal(null);
      setShowModal(false);
      fetchBusinessList();
    } catch (error) {
      console.error("Error in handleEditBusiness:", error);
    }
  };

  const handleDeleteBusiness = (id: number) => {
    setPendingDeleteId(id);
    setPopconfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    try {
      const token = localStorage.getItem("auth_token");
      await import('@/services/businessService').then(({ deleteBusiness }) => deleteBusiness(pendingDeleteId, token));
      fetchBusinessList();
      refreshBusinessCreationStatus();
    } catch (error) {
      console.error("Error in handleDeleteBusiness:", error);
    }
    setPopconfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleCategoryChange = async (businessId: string, selectedCategoryNames: string[]) => {
    try {
      // Find category IDs by selected names
      const selectedIds = categoryOptions
        .filter(category => selectedCategoryNames.includes(category.name))
        .map(category => category.id);

      // Find the business and update its categories
      const business = businessList.find(b => b.id === businessId);
      if (!business) return;

      const token = localStorage.getItem("auth_token");
      await updateBusiness({
        ...business,
        categoryIds: selectedIds
      }, token);

      fetchBusinessList();
    } catch (error) {
      console.error("Error updating business categories:", error);
    }
  };

  const handleCancelDelete = () => {
    setPopconfirmOpen(false);
    setPendingDeleteId(null);
  };

  const handleCollapseSave = async (businessId: string) => {
    // This is a placeholder - individual field saving would require more complex implementation
    // For now, we could show a message or just refresh the data
    console.log('Collapse save clicked for business:', businessId);
    // Show success message like when saving theme
    addNotification(t('notifications.success'), 'success');
    // Refresh the business list to ensure data is up to date
    fetchBusinessList();
  };

  const handleAddressClick = (address: string, city?: string) => {
    // Спробуємо розібрати адресу
    if (address.includes(',')) {
      const parts = address.split(',').map(part => part.trim());

      if (parts.length >= 3) {
        const addressCity = city || parts[0];
        const street = parts[1];
        const building = parts[2];
        openGoogleMaps(addressCity, street, building);
      } else if (parts.length === 2) {
        const addressCity = city || 'Ukraine';
        const street = parts[0];
        const building = parts[1];
        openGoogleMaps(addressCity, street, building);
      } else {
        const addressCity = city || 'Ukraine';
        openGoogleMaps(addressCity, address, '');
      }
    } else {
      const addressCity = city || 'Ukraine';
      openGoogleMaps(addressCity, address, '');
    }
  };

  return (
    <div className="settings-page">
      <div className="settings-card">
        <div className="header-row" >
          <h1 className="page-title">
            <i className="bi bi-building"></i>
            {t('profile.settings.business.title')}
          </h1>
          <button
            className={`btn-primary add-business-btn ${!businessCreationStatus?.can_create ? 'disabled' : ''}`}
            onClick={() => setShowModal(true)}
            disabled={!businessCreationStatus?.can_create || permissionsLoading}
            title={!businessCreationStatus?.can_create ? businessCreationStatus?.reason : undefined}
          >
            <i className="bi bi-plus-lg"></i>
            <span className="add-business-text">{t('businessSettings.addBusiness')}</span>
          </button>
        </div>
        <p className="page-description">
          {t('profile.settings.business.description')}
        </p>

        {/* Tab Navigation */}
        <div className="tabs-navigation">
          <button
            className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="bi bi-gear"></i>
            <span className="tab-text">
              {t('businessSettings.tabs.settings')}
            </span>
          </button>
          <button
            className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            <i className="bi bi-eye"></i>
            <span className="tab-text">{t('businessSettings.tabs.preview')}</span>
          </button>
        </div>

        <div className={`settings-content ${activeTab === 'preview' ? 'preview-mode' : ''} ${activeTab === 'settings' && businessList.filter(bsn => bsn && bsn.id).length === 0 ? 'no-businesses-mode' : ''}`}>
          {/* Modal components outside tabs so they work from both tabs */}
          <Popconfirm
            open={popconfirmOpen}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title={t('popconfirm.title')}
            subtitle={t('popconfirm.subtitle')}
            description={t('popconfirm.description')}
            cancelText={t('popconfirm.no')}
            okText={t('popconfirm.yes')}
            okButtonVariant="danger"
          />
          <BusinessModal
            show={showModal || editingBusinessLocal !== null}
            onClose={() => { setShowModal(false); setEditingBusiness(null); setEditingBusinessLocal(null); }}
            onSubmit={editingBusinessLocal ? handleEditBusiness : handleAddBusiness}
            initialData={editingBusinessLocal ? editingBusinessLocal : undefined}
            mode={editingBusinessLocal ? 'edit' : 'add'}
          />

          {activeTab === 'settings' && (
            <>
              {businessList.filter(bsn => bsn && bsn.id).length === 0 ? (
                <div className="no-businesses">
                  <i className="bi bi-building"></i>
                  <p>{t('businessSettings.noData')}</p>
                  <button
                    className={`btn-primary ${!businessCreationStatus?.can_create ? 'disabled' : ''}`}
                    onClick={() => setShowModal(true)}
                    disabled={!businessCreationStatus?.can_create || permissionsLoading}
                    title={!businessCreationStatus?.can_create ? businessCreationStatus?.reason : undefined}
                    style={{ marginTop: '16px' }}
                  >
                    {t('businessSettings.addBusiness')}
                  </button>
                </div>
              ) : (
                businessList.filter(bsn => bsn && bsn.id).map((bsn, idx) => (
                <Collapse
                  key={bsn?.id || idx}
                  title={
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <BusinessStatusIndicator isActive={!!bsn?.is_active} />
                      {bsn?.business_name || bsn?.name || 'Назва бізнесу'}
                      <button
                        className="edit-business-icon-btn"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        title={t('businessSettings.editBusiness')}
                        onClick={e => {
                          e.stopPropagation();
                          setEditingBusiness(bsn);
                          setEditingBusinessLocal(JSON.parse(JSON.stringify(bsn)));
                        }}
                      >
                        <i className="bi bi-pencil-square" style={{ fontSize: 18, color: '#007bff' }}></i>
                      </button>
                    </span>
                  }
                  onDelete={() => handleDeleteBusiness(bsn?.id)}
                  onSave={() => handleCollapseSave(bsn?.id)}
                >
                  {/* Master Business Checkbox */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        id={`isMasterBusiness-${bsn?.id || idx}`}
                        defaultChecked={bsn?.is_master_business || false}
                        onChange={(e) => {
                          const updatedBusiness = { ...bsn, is_master_business: e.target.checked };
                          setBusinessList(prev => prev.map(b => b.id === bsn.id ? updatedBusiness : b));
                        }}
                        style={{ cursor: 'pointer' }}
                      />
                      <i className="bi bi-person-workspace"></i>
                      {t('businessSettings.showAsMaster')}
                    </label>
                    <small style={{ color: '#666', marginLeft: '28px', display: 'block', marginTop: '4px' }}>
                      {t('businessSettings.showAsMasterDescription')}
                    </small>
                  </div>

                  {/* Experience Field - only show for master businesses */}
                  {bsn?.is_master_business && (
                    <div className="form-group">
                      <label htmlFor={`experience-${bsn?.id || idx}`}>
                        <i className="bi bi-award"></i>
                        {t('businessSettings.experience')}
                      </label>
                      <textarea
                        id={`experience-${bsn?.id || idx}`}
                        className="form-input"
                        defaultValue={bsn?.experience || ''}
                        placeholder={t('businessSettings.experiencePlaceholder')}
                        rows={3}
                        style={{ resize: 'vertical' }}
                        onChange={(e) => {
                          const updatedBusiness = { ...bsn, experience: e.target.value };
                          setBusinessList(prev => prev.map(b => b.id === bsn.id ? updatedBusiness : b));
                        }}
                      />
                    </div>
                  )}

                  {/* Галерея фото бізнесу - після назви */}
                  <div className="form-group">
                    <label>
                      <i className="bi bi-images"></i>
                      {t('businessSettings.gallery')}
                    </label>
                    <BusinessGallery
                      businessId={bsn.id}
                      initialPhotos={bsn.photos || []}
                      onChange={() => {
                        // Оновлюємо список бізнесів після зміни фото для оновлення прев'ю
                        fetchBusinessList();
                      }}
                    />
                  </div>

                  {/* Business Category */}
                  <div className="form-group">
                    <label>
                      <i className="bi bi-tags"></i>
                      {t('businessSettings.categories')}
                    </label>
                    <MultipleSelect
                      label={t('businessSettings.categories')}
                      options={categoryOptions}
                      selectedValues={categoryOptions.filter(cat => (bsn?.categoryIds || []).includes(cat.id)).map(cat => cat.name)}
                      onChange={(selected) => handleCategoryChange(bsn.id, selected)}
                    />
                  </div>

                  {/* Services Select */}
                  <div className="form-group">
                    <label>
                      <i className="bi bi-list-check"></i>
                      {t('businessSettings.services')}
                    </label>
                    <MultipleSelect
                      label={t('businessSettings.services')}
                      options={servicesOptions}
                      selectedValues={servicesOptions.filter(s => (bsn?.services || []).includes(s.id)).map(s => s.name)}
                      onChange={() => {}}
                    />
                  </div>

                  {/* City, Street, Building in one row with proportions 30% 50% 20% */}
                  <div className="form-group form-row">
                    <label>
                      <i className="bi bi-signpost"></i>
                      {t('businessSettings.address')}
                    </label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input
                        id={`businessCity-${bsn?.id || idx}`}
                        type="text"
                        className="form-input"
                        style={{ flex: '0 0 33%' }}
                        defaultValue={bsn?.city || ''}
                        placeholder={t('businessSettings.address')}
                      />
                      <input
                        id={`businessStreet-${bsn?.id || idx}`}
                        type="text"
                        className="form-input"
                        style={{ flex: '0 0 50%' }}
                        defaultValue={bsn?.street || ''}
                        placeholder={t('businessSettings.address')}
                      />
                      <input
                        id={`businessBuilding-${bsn?.id || idx}`}
                        type="text"
                        className="form-input"
                        style={{ flex: '0 0 20%' }}
                        defaultValue={bsn?.building || ''}
                        placeholder={t('businessSettings.address')}
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="form-group">
                    <label htmlFor={`businessPhone-${bsn?.id || idx}`}>
                      <i className="bi bi-telephone"></i>
                      {t('businessSettings.phone')}
                    </label>
                    <input
                      id={`businessPhone-${bsn?.id || idx}`}
                      type="text"
                      className="form-input"
                      defaultValue={bsn?.business_phone || bsn?.phone || ''}
                      placeholder={t('businessSettings.phone')}
                    />
                  </div>

                  {/* Website */}
                  <div className="form-group">
                    <label htmlFor={`website-${bsn?.id || idx}`}>
                      <i className="bi bi-globe"></i>
                      {t('businessSettings.website')}
                    </label>
                    <input
                      id={`website-${bsn?.id || idx}`}
                      type="url"
                      className="form-input"
                      defaultValue={bsn?.website || ''}
                      placeholder={t('businessSettings.website')}
                    />
                  </div>

                  {/* Social Media */}
                  <div className="form-group">
                    <label>
                      <i className="bi bi-share"></i>
                      {t('businessSettings.socialMedia')}
                    </label>
                    <div className="social-inputs">
                      <div className="social-input-wrapper">
                        <i className="bi bi-facebook social-icon facebook"></i>
                        <input
                          type="url"
                          className="form-input"
                          defaultValue={bsn?.facebook || ''}
                          placeholder={t('businessSettings.socialMedia')}
                        />
                      </div>
                      <div className="social-input-wrapper">
                        <i className="bi bi-instagram social-icon instagram"></i>
                        <input
                          type="url"
                          className="form-input"
                          defaultValue={bsn?.instagram || ''}
                          placeholder={t('businessSettings.socialMedia')}
                        />
                      </div>
                      <div className="social-input-wrapper">
                        <i className="bi bi-twitter social-icon twitter"></i>
                        <input
                          type="url"
                          className="form-input"
                          defaultValue={bsn?.twitter || ''}
                          placeholder={t('businessSettings.socialMedia')}
                        />
                      </div>
                      <div className="social-input-wrapper">
                        <i className="bi bi-linkedin social-icon linkedin"></i>
                        <input
                          type="url"
                          className="form-input"
                          defaultValue={bsn?.linkedin || ''}
                          placeholder={t('businessSettings.socialMedia')}
                        />
                      </div>
                    </div>
                  </div>

                </Collapse>
                ))
              )}
            </>
          )}

          {activeTab === 'preview' && (
            <div className="business-preview">
            {!previewDataLoaded ? (
              <div className="no-businesses">
                <i className="bi bi-arrow-repeat"></i>
                <p>{t('businessSettings.loading')}</p>
              </div>
            ) : businessList.length === 0 ? (
              <div className="no-businesses">
                <i className="bi bi-building"></i>
                <p>{t('profile.settings.business.noBusinesses')}</p>
              </div>
            ) : (
                <div className="salons-grid">
                  {businessList.filter(business => business && business.id).map((business) => (
                    <div
                      key={business.id}
                      className="salon-card"
                    >
                      {/* Edit and Delete Icons */}
                      <div className="salon-card-actions">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => {
                          setEditingBusiness(business);
                          setEditingBusinessLocal(JSON.parse(JSON.stringify(business)));
                        }}
                        title={t('businessSettings.editBusiness')}
                        aria-label={t('businessSettings.editBusiness')}
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteBusiness(business.id)}
                          title={t('businessSettings.deleteBusiness')}
                          aria-label={t('businessSettings.deleteBusiness')}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                      <div className="salon-image">
                        {(() => {
                          // Try to get image from photos array first, then fallback to logo
                          const photos = business.photos || [];
                          const firstPhoto = Array.isArray(photos) && photos.length > 0 ? photos[0] : null;
                          const imageUrl = firstPhoto || business.logo || business.business_logo;

                          return imageUrl ? (
                            <img src={imageUrl} alt={business.business_name || business.name} />
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240, background: '#f5f5f5' }}>
                              <ImageIcon style={{ fontSize: 96, color: '#bdbdbd' }} />
                            </div>
                          );
                        })()}
                        <div className="salon-rating">
                          <i className="bi bi-star-fill"></i>
                          <span>{business.rating || 0}</span>
                        </div>
                      </div>
                      <div className="salon-info">
                        <h3 className="salon-name">{business.business_name || business.name}</h3>
                        <div className="salon-categories">
                          {(() => {
                            const filtered = categoryOptions.filter(cat => business.categoryIds?.includes(cat.id));
                            return Array.isArray(business.categoryIds) && business.categoryIds.length > 0 && filtered.length > 0 ? (
                              filtered.map((cat, index) => (
                                <span key={index} className="category-badge">
                                  <i className="bi bi-tag"></i>
                                  {cat.name}
                                </span>
                              ))
                            ) : (
                              <span className="category-badge no-categories">
                                {t('businessSettings.noCategories')}
                              </span>
                            );
                          })()}
                        </div>
                        <p className="salon-address">
                          <i className="bi bi-geo-alt"></i>
                          <span
                            onClick={() => handleAddressClick(`${business.city || ''}, ${business.street || ''}, ${business.building || ''}`, business.city)}
                            className="address-link"
                            title={t('businessSettings.addressLink')}
                          >
                            {`${business.city || ''}, ${business.street || ''} ${business.building || ''}`.trim().replace(/^,|,$/, '')}
                          </span>
                        </p>
                        <p className="salon-phone">
                          <i className="bi bi-telephone"></i>
                          <a
                            href={`tel:${getCleanPhoneNumber(business.business_phone || business.phone)}`}
                            className="phone-link"
                            title={t('businessSettings.phoneLink')}
                          >
                            {formatPhoneNumber(business.business_phone || business.phone)}
                          </a>
                        </p>
                        <div className="salon-services">
                          {(Array.isArray(business.services) ? business.services.slice(0, 3) : []).map((serviceId: any, index: number) => {
                            const serviceName = servicesOptions.find(s => s.id === serviceId)?.name || serviceId;
                            return (
                              <span key={index} className="service-tag">
                                {serviceName}
                              </span>
                            );
                          })}
                          {Array.isArray(business.services) && business.services.length > 3 && (
                            <Tooltip
                              content={
                                <div>
                                  {business.services.slice(3, 8).map((serviceId: any, index: number) => {
                                    const serviceName = servicesOptions.find(s => s.id === serviceId)?.name || serviceId;
                                    return (
                                      <div key={index}>{serviceName}</div>
                                    );
                                  })}
                                </div>
                              }
                              position="top"
                            >
                              <span className="service-tag more">
                                +{business.services.length - 3}
                              </span>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BusinessSettings;
